import { formatDistanceToNow } from 'date-fns';
import { Download, FileText, Loader2 } from 'lucide-react';
import React, { useState } from 'react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  useGetAvailableWorkspacesForImportQuery,
  useImportExhibitsMutation,
} from '@/services/api/demandReviewService';

interface ImportExhibitsDialogProps {
  matterId: string;
  workspaceId: string;
  trigger?: React.ReactNode;
  onSuccess?: () => void;
}

export const ImportExhibitsDialog = ({
  matterId,
  workspaceId,
  trigger,
  onSuccess,
}: ImportExhibitsDialogProps) => {
  const [open, setOpen] = useState(false);
  const [selectedWorkspace, setSelectedWorkspace] = useState<{
    id: string;
    name: string;
    type: 'demand_workspace' | 'demand_review';
    exhibit_count: number;
  } | null>(null);

  const { data: availableWorkspaces, isLoading: isLoadingWorkspaces } =
    useGetAvailableWorkspacesForImportQuery(
      { matterId, workspaceId },
      { skip: !open },
    );

  const [importExhibits, { isLoading: isImporting }] =
    useImportExhibitsMutation();

  const handleImport = async () => {
    if (!selectedWorkspace) return;

    try {
      const result = await importExhibits({
        matterId,
        workspaceId,
        sourceWorkspaceId: selectedWorkspace.id,
        sourceType: selectedWorkspace.type,
      }).unwrap();

      toast.success(`Imported ${result.imported_count} exhibits`);
      setOpen(false);
      setSelectedWorkspace(null);
      onSuccess?.();
    } catch (error) {
      console.error('Failed to import exhibits:', error);
      toast.error('Failed to import exhibits');
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      setSelectedWorkspace(null);
    }
  };

  const typeLabel = (type: string) =>
    type === 'demand_workspace' ? 'Demand Workspace' : 'Document Review';

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm" className="gap-2">
            <Download className="h-4 w-4" />
            Import Exhibits
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Import Exhibits</DialogTitle>
          <DialogDescription>
            Select a workspace to import exhibits from. Exhibits that already
            exist will be skipped.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {isLoadingWorkspaces && (
            <div className="flex h-32 items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          )}
          {!isLoadingWorkspaces &&
            (!availableWorkspaces || availableWorkspaces.length === 0) && (
              <div className="flex h-32 flex-col items-center justify-center text-center text-muted-foreground">
                <FileText className="mb-2 h-8 w-8" />
                <p>No workspaces with exhibits found</p>
                <p className="text-sm">
                  Add exhibits to a demand workspace first
                </p>
              </div>
            )}
          {!isLoadingWorkspaces &&
            availableWorkspaces &&
            availableWorkspaces.length > 0 && (
              <ScrollArea className="max-h-[300px]">
                <div
                  className="space-y-2 pr-4"
                  role="listbox"
                  aria-label="Available workspaces"
                >
                  {availableWorkspaces.map((ws) => (
                    <button
                      key={ws.id}
                      type="button"
                      role="option"
                      aria-selected={selectedWorkspace?.id === ws.id}
                      className={`w-full cursor-pointer rounded-lg border p-3 text-left transition-colors ${
                        selectedWorkspace?.id === ws.id
                          ? 'border-primary bg-primary/5'
                          : 'hover:border-primary/50 hover:bg-muted/50'
                      }`}
                      onClick={() => setSelectedWorkspace(ws)}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="font-medium line-clamp-2">{ws.name}</p>
                          <p className="mt-1 text-sm text-muted-foreground">
                            {formatDistanceToNow(new Date(ws.created_at), {
                              addSuffix: true,
                            })}
                          </p>
                        </div>
                        <div className="flex flex-shrink-0 flex-col items-end gap-1">
                          <Badge
                            variant="secondary"
                            className="text-xs whitespace-nowrap"
                          >
                            {typeLabel(ws.type)}
                          </Badge>
                          <span className="text-sm text-muted-foreground whitespace-nowrap">
                            {ws.exhibit_count} exhibit
                            {ws.exhibit_count !== 1 ? 's' : ''}
                          </span>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </ScrollArea>
            )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleImport}
            disabled={!selectedWorkspace || isImporting}
          >
            {isImporting && (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Importing...
              </>
            )}
            {!isImporting &&
              selectedWorkspace &&
              `Import ${selectedWorkspace.exhibit_count} Exhibit${selectedWorkspace.exhibit_count !== 1 ? 's' : ''}`}
            {!isImporting && !selectedWorkspace && 'Select a Workspace'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
